import { apiSlice } from '../app/api/apiSlice';

export const QuizApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getQuestions: builder.query({
      query: (lang) => ({ url: `quiz?lang=${lang}` }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    answers: builder.mutation({
      query: (payload) => ({
        url: 'quiz/answersweb',
        method: 'POST',
        body: { ...payload },
      }),
    }),
    answersWeb: builder.query({
      query: (payload) => ({
        url: 'quiz/answersweb',
        method: 'POST',
        body: { ...payload },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const quizId = Number(queryArgs?.quiz_id || 0);
        const lang = queryArgs?.lang || 'en';

        if (quizId > 0) {
          return `${endpointName}:${quizId}:${lang}`;
        }

        return `${endpointName}:${JSON.stringify(queryArgs || {})}`;
      },
      keepUnusedDataFor: 300,
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    saveIncompleteQuiz: builder.mutation({
      query: (payload) => ({
        url: 'quiz/incomplete',
        method: 'POST',
        body: { ...payload },
      }),
    }),
    saveQuizProgress: builder.mutation({
      query: (payload) => ({
        url: 'quiz/progress',
        method: 'POST',
        body: { ...payload },
      }),
    }),
    addOmnisendEvent: builder.mutation({
      query: (payload) => ({
        url: 'omnisend-event',
        method: 'POST',
        keepalive: true,
        body: { ...payload },
      }),
    }),
    getIncompleteQuiz: builder.query({
      query: (id) => ({ url: `quiz/incomplete?id=${id}` }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    getClassmedResult: builder.query({
      query: ({ classmedId, lang = 'en' }) => {
        const queryString = new URLSearchParams({
          classmedId,
          lang,
        }).toString();

        return { url: `quiz/classmed-result?${queryString}` };
      },
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    getAssets: builder.query({
      query: (params) => {
        const queryParams = typeof params === 'string' ? { section: params } : params || {};
        const queryString = new URLSearchParams(
          Object.entries(queryParams).filter(([, value]) => value !== undefined && value !== null && value !== ''),
        ).toString();

        return { url: `asset?${queryString}` };
      },
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    getIngredients: builder.query({
      // `lang` localises ingredient / description / scientific_name server-side,
      // with an English fallback per row. Without it the catalog comes back in
      // English, which is how a manually added ingredient ended up rendering in
      // English on the German and French sites (SHI-554).
      query: (lang) => ({
        url: lang ? `ingredient/all?lang=${encodeURIComponent(lang)}` : `ingredient/all`,
      }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    updateQuizIngredients: builder.mutation({
      query: (payload) => ({
        url: 'ingredient/updateQuizIngredients',
        method: 'POST',
        body: { ...payload },
      }),
    }),
    getIngredientPrices: builder.query({
      // `lang` is undefined on the first render, which used to produce a literal
      // `?lang=undefined`. The server falls back to English for an unrecognised
      // value, so it degraded rather than broke — but it cost a wasted request
      // under its own cache key and could briefly show English ingredient names
      // on a non-English page before the real language resolved.
      query: (lang) => ({
        url: lang ? `ingredientPrices?lang=${encodeURIComponent(lang)}` : `ingredientPrices`,
      }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    getProductPrices: builder.query({
      query: (params = {}) => {
        const queryString = new URLSearchParams(
          Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
        ).toString();

        return { url: queryString ? `productPrices?${queryString}` : 'productPrices' };
      },
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    createCheckoutSession: builder.mutation({
      query: (payload) => ({
        url: 'checkout/session',
        method: 'POST',
        body: { ...payload },
      }),
    }),
    getClassmedPriceQuote: builder.mutation({
      query: (payload) => ({
        url: 'classmed/price-quote',
        method: 'POST',
        body: { ...payload },
      }),
    }),
    updateClassmedFormula: builder.mutation({
      query: (payload) => ({
        url: 'classmed/formula',
        method: 'PUT',
        body: { ...payload },
      }),
    }),
    createClassmedCheckoutSession: builder.mutation({
      query: (payload) => ({
        url: 'classmed/checkout-session',
        method: 'POST',
        body: { ...payload },
      }),
    }),
    getClassmedOrderStatus: builder.query({
      query: (sessionId) => ({
        url: `classmed/order-status?sessionId=${encodeURIComponent(sessionId)}`,
      }),
    }),
    getNutritionists: builder.query({
      query: () => ({ url: `nutritionist` }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    updateQuizUpdatedAt: builder.mutation({
      query: (payload) => ({
        url: '/quiz/updated_at',
        method: 'PUT',
        body: { ...payload },
      }),
    }),
    omnisendPlacedOrderEvent: builder.mutation({
      query: (payload) => ({
        url: '/order/omnisend-placed-order-event',
        method: 'POST',
        body: { ...payload },
      }),
    }),
  }),
});

export const {
  useGetQuestionsQuery,
  useAnswersMutation,
  useLazyAnswersWebQuery,
  useGetIncompleteQuizQuery,
  useGetClassmedResultQuery,
  useSaveIncompleteQuizMutation,
  useSaveQuizProgressMutation,
  useGetAssetsQuery,
  useGetIngredientsQuery,
  useUpdateQuizIngredientsMutation,
  useGetIngredientPricesQuery,
  useGetProductPricesQuery,
  useCreateCheckoutSessionMutation,
  useGetClassmedPriceQuoteMutation,
  useUpdateClassmedFormulaMutation,
  useCreateClassmedCheckoutSessionMutation,
  useGetClassmedOrderStatusQuery,
  useLazyGetClassmedOrderStatusQuery,
  useGetNutritionistsQuery,
  useAddOmnisendEventMutation,
  useUpdateQuizUpdatedAtMutation,
  useOmnisendPlacedOrderEventMutation,
} = QuizApiSlice;
